import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

/** Version anglaise de `slugs/mipcom.ts`. Mêmes faits, mêmes tarifs réels. */
const mipcom: ChauffeurPage = {
  slug: "mipcom",
  title: "Private chauffeur MIPCOM Cannes – Executive VTC",
  subtitle:
    "Private chauffeur during MIPCOM: delegation transfers, back-to-back buyer meetings and networking dinners around the Palais des Festivals.",
  keywords: [
    "mipcom chauffeur",
    "mipcom cannes private chauffeur",
    "executive car service Cannes",
  ],
  hero: {
    title: "Private chauffeur during MIPCOM",
    subtitle:
      "The international content market, held in Cannes every October: an executive chauffeur to hold a back-to-back meeting schedule.",
    keywords: [
      "MIPCOM chauffeur",
      "executive car service Cannes",
      "Palais des Festivals chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during MIPCOM",
    badges: ["Delegation transfers", "Back-to-back meetings", "Evening runs"],
  },
  benefits: {
    heading: "Why an executive chauffeur at MIPCOM",
    items: [
      {
        title: "A buyer schedule that holds",
        description:
          "Stands at the Palais, private hotel suites and screenings follow each other by the quarter hour. A dedicated chauffeur absorbs the delays without shifting your day.",
      },
      {
        title: "Multi-stop roadshows",
        description:
          "Distributors, broadcasters and producers all host in different places: one car, one chauffeur, one thread through the whole day.",
      },
      {
        title: "Business airport transfers",
        description:
          "Nice Côte d'Azur concentrates international arrivals in October: flight tracking, name-board welcome and a direct run to the Croisette. From 100 EUR.",
      },
      {
        title: "Market dinners and evenings",
        description:
          "Catalogue receptions, closing dinners and partner parties end late. Your late-night return is covered.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during MIPCOM",
    services: [
      {
        title: "Nice airport transfer",
        text: "Nice to Cannes with flight tracking, from 100 EUR, through the market's arrival peak in October.",
      },
      {
        title: "Hourly and daily hire",
        text: "A dedicated chauffeur by the hour or the day, from 240 EUR for three hours, to chain meetings at the Palais and business lunches.",
      },
      {
        title: "Buyer roadshow",
        text: "Multi-stop routes between the Palais, the Croisette hotel suites and the distributors' temporary offices.",
      },
      {
        title: "Dinners and closing evenings",
        text: "Networking receptions and negotiation dinners on the Croisette, with the chauffeur waiting and a late return.",
      },
      {
        title: "Delegation shuttles",
        text: "Multi-vehicle coordination for channel, studio and institutional delegations, from the 7-seat van to the 16-seat VIP minibus.",
      },
    ],
  },
  locations: {
    heading: "Key points during MIPCOM",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description:
          "Stands, national pavilions, screening rooms and meeting areas.",
      },
      {
        name: "La Croisette",
        description:
          "Hotel suites taken over by distributors, and reception terraces.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description:
          "Terminals 1 and 2, international arrivals concentrated in October.",
      },
      {
        name: "Mougins and Mandelieu",
        description: "Delegation accommodation and quieter client dinners.",
      },
      {
        name: "Monaco",
        description:
          "Meetings and receptions in the Principality, 250 EUR for the transfer from Cannes.",
      },
    ],
  },
  generic: {
    heading: "MIPCOM seen from the driver's seat",
    subheading:
      "Every October, the international content market fills Cannes with back-to-back buying and selling meetings.",
    paragraphs: [
      "Each October, MIPCOM brings the world's broadcasters, distributors, studios and platforms to the Palais des Festivals. Meetings are set by the quarter hour across stands, screening rooms and Croisette hotel suites: a transport delay costs a negotiation, not just a meeting.",
      "Arrivals concentrate at Nice Côte d'Azur airport, 27 km and about thirty minutes from Cannes. Flight tracking, a name-board welcome at the terminal and a direct run to your hotel avoid the queue at the exact moment the whole industry lands. The transfer is 100 EUR in a Mercedes E-Class saloon, fixed in advance.",
      "Beyond the transfer, hourly hire turns the day into a continuous roadshow: several buyer meetings, a lunch, a screening and a closing dinner chained with the same chauffeur and the same car, from 240 EUR for three hours, through to the late Croisette evenings.",
    ],
    bullets: [
      "Multi-stop roadshow",
      "Airport transfer from 100 EUR",
      "Closing dinners",
    ],
  },
  process: {
    heading: "Book a chauffeur for MIPCOM",
    steps: [
      "Send us your market dates, your hotel and the list of your meetings.",
      "We build the multi-stop route and set the timings around your schedule.",
      "Your chauffeur arrives in uniform, with flight tracking for your Nice arrival.",
      "Daily and evening hire to hold screenings, meetings and dinners.",
    ],
  },
  faq: [
    {
      q: "How much does a private chauffeur cost during MIPCOM?",
      a: "The transfer between Nice airport and Cannes is 100 EUR in a Mercedes E-Class saloon, fixed in advance. Hourly hire with a dedicated chauffeur starts at 240 EUR for three hours in the same category, and 600 EUR for an eight-hour day. Vans and VIP minibuses for delegations are priced by category.",
    },
    {
      q: "How do I book an executive chauffeur during MIPCOM?",
      a: "Book ahead: in October demand in Cannes rises sharply and cars go quickly. Send your dates, your hotel and your meetings so we can hold a dedicated chauffeur and prepare a multi-stop route.",
    },
    {
      q: "Can you chain several buyer meetings in one day?",
      a: "Yes, that is the typical MIPCOM pattern. Hourly hire keeps the same chauffeur and the same car for a full roadshow between the Palais, the hotel suites and the temporary offices, with no need to book each leg.",
    },
    {
      q: "Do you handle Nice airport arrivals during the market?",
      a: "Yes, with flight tracking and a name-board welcome at terminal 1 or 2. In October Nice Côte d'Azur sees a peak of international arrivals: we anticipate and adjust the pick-up time if your flight is late, with 30 minutes of courtesy waiting on arrival.",
    },
    {
      q: "Can you carry whole delegations?",
      a: "Yes. The Mercedes V-Class van takes 7 passengers and the Mercedes Sprinter VIP minibus up to 16. For larger delegations we coordinate several vehicles with dedicated chauffeurs for the whole market.",
    },
  ],
  relatedLinks: [
    {
      label: "Book a Cannes to Nice airport transfer",
      href: "/en/chauffeur/transfers/cannes-nice-airport",
    },
    {
      label: "Private chauffeur for MIPIM, the March market",
      href: "/en/chauffeur/mipim",
    },
    {
      label: "Private chauffeur for business",
      href: "/en/chauffeur/business",
    },
    {
      label: "Check availability",
      href: "/en/reservation",
    },
  ],
  cta: {
    title: "Your executive chauffeur for MIPCOM",
    text: "Airport transfers from 100 EUR, buyer roadshows and closing dinners: book a dedicated chauffeur for the market.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "MIPCOM Cannes Chauffeur — Executive car from 100 EUR",
    description:
      "Private chauffeur during MIPCOM in Cannes: Nice airport transfer from 100 EUR, hourly hire from 240 EUR, buyer roadshows and dinners. Book online.",
    canonical: "/en/chauffeur/mipcom",
  },
};

export default mipcom;
